import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/auth";

const TONES = ["live", "wip", "queued", "dim"];
const FORMATS = ["currency", "int", "percent"];
const ICONS = ["dollar", "briefcase", "users", "receipt"];

/**
 * /admin/nds — schema editor for NDS-1.
 *
 * Lets partners edit every label, KPI definition, workflow step, capability
 * tile and demo feed per vertical, then persist to the backend. The rest of
 * the app hot-reloads because NexOsProvider re-fetches /api/nex-os/schema.
 */
export default function NdsSchemaEditorPage() {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeVertical, setActiveVertical] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/nex-os/schema");
      setSchema(data.schema);
    } catch (e) {
      toast.error("Failed to load schema");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!schema) return;
    setSaving(true);
    try {
      await apiClient.put("/admin/nex-os/schema", { schema });
      toast.success("Schema saved — layout will refresh across the OS");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Reset schema to factory defaults? This overwrites all current edits.")) return;
    setSaving(true);
    try {
      const { data } = await apiClient.post("/admin/nex-os/schema/reset");
      setSchema(data.schema);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const currentV = useMemo(() => schema?.verticals?.[activeVertical], [schema, activeVertical]);

  const updateVertical = (patch) => {
    setSchema((s) => {
      const next = { ...s, verticals: s.verticals.map((v, i) => i === activeVertical ? { ...v, ...patch } : v) };
      return next;
    });
  };

  const updateKpi = (idx, patch) => {
    updateVertical({ kpis: currentV.kpis.map((k, i) => i === idx ? { ...k, ...patch } : k) });
  };
  const updateStep = (idx, patch) => {
    updateVertical({ steps: currentV.steps.map((k, i) => i === idx ? { ...k, ...patch } : k) });
  };
  /**
   * Capability edits are TEMPLATE edits — the 20-tile grid is one universal
   * roadmap. Patch `schema.capabilities_template` (the persisted source of
   * truth) and mirror to every vertical's derived `capabilities` array so
   * the editor UI reflects the change immediately. The backend derives
   * capabilities from the template on read and strips the mirror on save,
   * so this stays consistent across every vertical tab.
   */
  const updateCap = (idx, patch) => {
    setSchema((s) => {
      const nextTpl = (s.capabilities_template || []).map((k, i) => i === idx ? { ...k, ...patch } : k);
      const nextVerticals = (s.verticals || []).map((v) => ({
        ...v,
        capabilities: Array.isArray(v.capabilities)
          ? v.capabilities.map((k, i) => i === idx ? { ...k, ...patch } : k)
          : v.capabilities,
      }));
      return { ...s, capabilities_template: nextTpl, verticals: nextVerticals };
    });
  };
  const updateFeed = (idx, patch) => {
    updateVertical({ live_feed_events: (currentV.live_feed_events || []).map((k, i) => i === idx ? { ...k, ...patch } : k) });
  };
  const addFeed = () => updateVertical({ live_feed_events: [...(currentV.live_feed_events || []), { time: "JUST NOW", event: "New Event", details: "", status: "info" }] });
  const removeFeed = (idx) => updateVertical({ live_feed_events: (currentV.live_feed_events || []).filter((_, i) => i !== idx) });

  // Update KPI value/format handles both new (`value`) and legacy (`demo`) fields.
  const updateKpiValue = (idx, val) => updateKpi(idx, { value: Number(val) || 0 });

  if (loading || !schema) {
    return (
      <div className="min-h-screen bg-nex-bg text-nex-silver grid place-items-center font-mono text-sm" data-testid="nds-editor-loading">
        Loading schema…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nex-bg text-nex-silver" data-testid="nds-editor-page">
      {/* Top bar */}
      <header className="border-b border-nex-border/60 bg-nex-surface/60 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/os" data-testid="nds-editor-back" className="flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-nex-chrome hover:text-nex-silver">
              <ArrowLeft className="w-4 h-4" /> Back to OS
            </Link>
            <div className="h-4 w-px bg-nex-border" />
            <div>
              <div className="font-mono text-[0.65rem] tracking-[0.3em] text-nex-gold uppercase">
                // NDS-1 SCHEMA EDITOR
              </div>
              <div className="font-serif text-xl">Edit every label, KPI, step & capability</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="nds-editor-reset"
              onClick={resetToDefaults}
              disabled={saving}
              className="px-3 py-2 border border-nex-border text-nex-chrome hover:border-red-500/50 hover:text-red-400 font-mono text-xs tracking-widest uppercase transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button
              type="button"
              data-testid="nds-editor-save"
              onClick={save}
              disabled={saving}
              className="tick-btn-primary disabled:opacity-50"
              style={{ padding: "10px 18px", fontSize: "0.75rem", letterSpacing: "0.14em", textTransform: "uppercase" }}
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Schema"}
            </button>
          </div>
        </div>
      </header>

      {/* Vertical tabs */}
      <div className="border-b border-nex-border/60 bg-nex-surface/30">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-wrap gap-2">
          {schema.verticals.map((v, i) => (
            <button
              key={v.id}
              type="button"
              data-testid={`nds-editor-vertical-${v.id}`}
              onClick={() => setActiveVertical(i)}
              className={`font-mono text-xs tracking-widest uppercase px-4 py-2 border ${activeVertical === i ? "bg-nex-gold text-nex-navy border-nex-gold" : "border-nex-border/60 text-nex-chrome hover:text-nex-silver"}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-8">
        {/* Vertical basics */}
        <section className="card-industrial p-5 space-y-3" data-testid="nds-editor-basics">
          <div className="mono-label">// VERTICAL BASICS</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <LabeledInput label="Label" value={currentV.label} onChange={(v) => updateVertical({ label: v })} testId="nds-editor-vertical-label" />
            <LabeledInput label="Tagline" value={currentV.tagline} onChange={(v) => updateVertical({ tagline: v })} testId="nds-editor-vertical-tagline" />
            <LabeledInput label="Accent" value={currentV.accent || ""} onChange={(v) => updateVertical({ accent: v })} testId="nds-editor-vertical-accent" />
          </div>
        </section>

        {/* KPIs */}
        <section className="card-industrial p-5 space-y-3" data-testid="nds-editor-kpis">
          <div className="mono-label">// TIER 1 · KPI PULSE (4)</div>
          <div className="space-y-2">
            {currentV.kpis.map((k, i) => (
              <div key={k.id} className="grid grid-cols-1 md:grid-cols-[80px_minmax(0,1fr)_120px_120px_140px] gap-2 items-center" data-testid={`nds-editor-kpi-${k.id}`}>
                <div className="font-mono text-[0.7rem] text-nex-muted uppercase">{k.id}</div>
                <LabeledInput label="Label" value={k.label} onChange={(v) => updateKpi(i, { label: v })} compact />
                <SelectField label="Icon" value={k.icon} options={ICONS} onChange={(v) => updateKpi(i, { icon: v })} compact />
                <SelectField label="Format" value={k.format} options={FORMATS} onChange={(v) => updateKpi(i, { format: v })} compact />
                <LabeledInput label="Live value" value={String(k.value ?? k.demo ?? "")} onChange={(v) => updateKpiValue(i, v)} compact testId={`nds-editor-kpi-value-${k.id}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="card-industrial p-5 space-y-3" data-testid="nds-editor-steps">
          <div className="mono-label">// TIER 2 · 10-STEP REVENUE FLOW</div>
          <div className="space-y-2">
            {currentV.steps.map((s, i) => (
              <div key={s.id} className="grid grid-cols-1 md:grid-cols-[70px_minmax(0,2fr)_minmax(0,2fr)_minmax(0,3fr)_90px] gap-2 items-start" data-testid={`nds-editor-step-${s.id}`}>
                <div className="font-mono text-[0.7rem] text-nex-muted uppercase pt-3">STEP {s.id}</div>
                <LabeledInput label="Title" value={s.title} onChange={(v) => updateStep(i, { title: v })} compact />
                <LabeledInput label="Status badge" value={s.status_badge || ""} onChange={(v) => updateStep(i, { status_badge: v })} compact testId={`nds-editor-step-badge-${s.id}`} />
                <LabeledInput label="Coaching" value={s.coaching || ""} onChange={(v) => updateStep(i, { coaching: v })} compact />
                <LabeledInput label="Score" value={String(s.score ?? 10)} onChange={(v) => updateStep(i, { score: Number(v) || 10 })} compact />
              </div>
            ))}
          </div>
        </section>

        {/* Capabilities */}
        <section className="card-industrial p-5 space-y-3" data-testid="nds-editor-caps">
          <div className="mono-label">// TIER 3 · 20-CAPABILITY ROADMAP</div>
          <div className="space-y-2">
            {currentV.capabilities.map((c, i) => (
              <div key={c.id} className="grid grid-cols-1 md:grid-cols-[70px_minmax(0,2fr)_minmax(0,2fr)_140px] gap-2 items-center" data-testid={`nds-editor-cap-${c.id}`}>
                <div className="font-mono text-[0.7rem] text-nex-muted uppercase">{String(i + 1).padStart(2, "0")}</div>
                <LabeledInput label="Label" value={c.label} onChange={(v) => updateCap(i, { label: v })} compact />
                <LabeledInput label="Status" value={c.status} onChange={(v) => updateCap(i, { status: v })} compact />
                <SelectField label="Tone" value={c.tone} options={TONES} onChange={(v) => updateCap(i, { tone: v })} compact />
              </div>
            ))}
          </div>
        </section>

        {/* Live feed events */}
        <section className="card-industrial p-5 space-y-3" data-testid="nds-editor-feed">
          <div className="flex items-center justify-between">
            <div className="mono-label">// LIVE SYSTEM FEED · STRUCTURED EVENTS</div>
            <button
              type="button"
              data-testid="nds-editor-feed-add"
              onClick={addFeed}
              className="font-mono text-[0.65rem] tracking-widest uppercase px-3 py-1 border border-nex-border/60 text-nex-chrome hover:text-nex-gold hover:border-nex-gold/50 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {(currentV.live_feed_events || []).map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[110px_minmax(0,1fr)_minmax(0,2fr)_120px_44px] gap-2 items-end" data-testid={`nds-editor-feed-row-${i}`}>
                <LabeledInput label="Time" value={row.time || ""} onChange={(v) => updateFeed(i, { time: v })} compact />
                <LabeledInput label="Event" value={row.event || ""} onChange={(v) => updateFeed(i, { event: v })} compact />
                <LabeledInput label="Details" value={row.details || ""} onChange={(v) => updateFeed(i, { details: v })} compact />
                <SelectField label="Status" value={row.status || "info"} options={["success", "info", "warning", "danger"]} onChange={(v) => updateFeed(i, { status: v })} compact />
                <button
                  type="button"
                  onClick={() => removeFeed(i)}
                  className="w-9 h-9 grid place-items-center border border-nex-border/60 text-nex-chrome hover:text-red-400 hover:border-red-500/50"
                  aria-label="Remove"
                  data-testid={`nds-editor-feed-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function LabeledInput({ label, value, onChange, compact, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-muted uppercase">{label}</span>
      <input
        data-testid={testId}
        className="input-industrial"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={compact ? { padding: "10px 12px", fontSize: "0.85rem" } : undefined}
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange, compact }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-muted uppercase">{label}</span>
      <select
        className="input-industrial"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={compact ? { padding: "10px 12px", fontSize: "0.85rem" } : undefined}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
